import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  PieChart,
  Pie,
  Cell,
  Label,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from 'recharts';

// Redux hooks and selectors
import { useAppSelector } from '../../redux/hooks/useAppSelector';
import { selectIsRTL } from '../../redux/selectors/displaySettingsSelectors';

// Custom hooks
import { useChartConfig } from './hooks/useChartConfig';
import { usePieActiveCell } from './hooks/usePieActiveCell';

// Shared components
import { ChartContainer } from './shared/ChartContainer';
import { ChartContent } from './shared/ChartContent';
import { ChartFooter } from './shared/ChartFooter';
import { IWeeklyLogAnalyticsResponse } from '../../common/interfaces/WeeklyLogAnalyticsResponse.interface';

interface WeeklyLogGenderTabProps {
  analyticsData: IWeeklyLogAnalyticsResponse;
}

export const WeeklyLogGenderTab: React.FC<WeeklyLogGenderTabProps> = ({
  analyticsData,
}) => {
  const { t } = useTranslation();
  const isRTL = useAppSelector(selectIsRTL);
  const config = useChartConfig();

  // Hovered slice behavior
  const { getCellProps } = usePieActiveCell();

  if (
    !analyticsData.totalLogs.genderDistribution ||
    analyticsData.totalLogs.genderDistribution.length === 0
  ) {
    return (
      <ChartContainer>
        <div className="p-4 text-center">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('analytics.labels.genderDistribution')}
          </h2>
          <p className="text-sm text-gray-700">{t('analytics.noData')}</p>
        </div>
      </ChartContainer>
    );
  }

  const genderData = analyticsData.totalLogs.genderDistribution
    .filter(
      (item) =>
        item.value?.toLowerCase() === 'male' ||
        item.value?.toLowerCase() === 'female'
    )
    .map((item) => ({
      name: t(`demographics.gender.${item.value?.toLowerCase()}`),
      value: item.count,
    }));

  const COLORS = [config.colors.primary, config.colors.secondary];

  return (
    <ChartContainer>
      {/* Header */}
      <div className="p-4 text-center">
        <h2 className="text-lg font-semibold text-gray-900">
          {t('analytics.labels.genderDistribution')}
        </h2>
        <p className="text-sm text-gray-700">
          {t('analytics.genderDistributionByActivity')}
        </p>
      </div>

      {/* Chart */}
      <ChartContent>
        <style>
          {`
            .recharts-legend-item .recharts-surface {
              margin-${isRTL ? 'left' : 'right'}: 8px !important;
            }
          `}
        </style>
        <ResponsiveContainer
          width="100%"
          height={360}
          style={config.styles.noOutline}
        >
          <PieChart onClick={undefined} style={config.styles.noOutline}>
            <Pie
              data={genderData}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={100}
              dataKey="value"
              onClick={undefined}
              style={config.styles.noOutline}
            >
              {genderData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                  {...getCellProps(index, {
                    suppressActive:
                      genderData.length === 1 ||
                      entry.value ===
                        genderData.reduce((acc, curr) => acc + curr.value, 0),
                  })}
                />
              ))}
              <Label
                content={({ viewBox }) => {
                  if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                    return (
                      <text
                        x={viewBox.cx}
                        y={viewBox.cy}
                        textAnchor="middle"
                        dominantBaseline="central"
                      >
                        <tspan
                          x={viewBox.cx}
                          y={(viewBox.cy || 0) - 25}
                          className="fill-gray-900 text-2xl font-bold"
                        >
                          {genderData.reduce(
                            (acc, curr) => acc + curr.value,
                            0
                          )}
                        </tspan>
                        <tspan
                          x={viewBox.cx}
                          y={(viewBox.cy || 0) + 5}
                          className="fill-gray-700 text-sm"
                        >
                          {t('dashboard.dataLabels.participants')}
                        </tspan>
                      </text>
                    );
                  }
                }}
              />
            </Pie>
            <Tooltip {...config.tooltip} />
            <Legend
              {...config.legend}
              iconType="rect"
              layout="horizontal"
              align="center"
              verticalAlign="bottom"
            />
          </PieChart>
        </ResponsiveContainer>
      </ChartContent>

      {/* Footer */}
      <ChartFooter>
        {t('demographics.ageGroupsDistributionShowing')}
      </ChartFooter>
    </ChartContainer>
  );
};
